import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, ShieldCheck, Package } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { formatPrice } from "@/lib/constants";
import api from "@/lib/api";

export default function Profile() {
  const { user, logout } = useAuth();
  const { currency, setCurrency } = useCurrency();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
  }, []);

  return (
    <>
      <AppHeader title="Mi Perfil" />
      <main className="page-scroll px-4 pt-3" data-testid="profile-page">
        <div className="card-clean p-4 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[#0A2A66] text-white flex items-center justify-center font-heading text-xl font-bold">
            {(user?.name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-[#0A2A66] truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            {user?.role === "admin" && (
              <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider bg-[#F5B914] text-[#0A2A66] px-2 py-0.5 rounded-full">
                Administrador
              </span>
            )}
          </div>
        </div>

        <div className="card-clean p-4 mt-3">
          <h3 className="font-heading font-bold text-[#0A2A66] text-sm">Datos personales</h3>
          <div className="mt-2 text-sm space-y-1 text-slate-600">
            <div><span className="text-slate-400">WhatsApp:</span> {user?.whatsapp}</div>
            <div><span className="text-slate-400">Ciudad:</span> {user?.city}</div>
            <div><span className="text-slate-400">País:</span> {user?.country}</div>
          </div>
        </div>

        <div className="card-clean p-4 mt-3">
          <h3 className="font-heading font-bold text-[#0A2A66] text-sm">Moneda de visualización</h3>
          <div className="mt-2 flex gap-2">
            {["GTQ", "USD"].map((c) => (
              <button
                key={c}
                onClick={() => setCurrency(c)}
                data-testid={`currency-${c}`}
                className={`flex-1 py-2 rounded-full text-sm font-semibold border ${
                  currency === c ? "bg-[#0A2A66] text-white border-[#0A2A66]" : "bg-white text-[#0A2A66] border-slate-200"
                }`}
              >
                {c === "GTQ" ? "Q Quetzal" : "$ USD"}
              </button>
            ))}
          </div>
        </div>

        {user?.role === "admin" && (
          <Link to="/admin" data-testid="admin-panel-link" className="card-clean mt-3 p-4 flex items-center gap-3 hover:border-[#1E5FD9] transition">
            <ShieldCheck className="text-[#F5B914]" />
            <div className="flex-1">
              <div className="font-heading font-bold text-[#0A2A66]">Panel de administrador</div>
              <div className="text-xs text-slate-500">Gestiona programas, precios y contenido</div>
            </div>
            <span className="text-[#1E5FD9]">→</span>
          </Link>
        )}

        <div className="mt-3">
          <h3 className="font-heading font-bold text-[#0A2A66] text-sm mb-2">Mis pedidos</h3>
          {orders.length === 0 ? (
            <div className="card-clean p-4 text-sm text-slate-500 text-center">Aún no tienes pedidos.</div>
          ) : (
            orders.map((o) => (
              <div key={o.id} className="card-clean p-3 mb-2" data-testid={`order-${o.id}`}>
                <div className="flex items-center gap-2">
                  <Package size={16} className="text-[#1E5FD9]" />
                  <span className="font-mono font-bold text-sm text-[#0A2A66]">{o.reference}</span>
                  <span className="badge-cat ml-auto">{o.status}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">{o.items.length} programa(s) · {formatPrice(o.total_usd, currency)}</div>
              </div>
            ))
          )}
        </div>

        <button
          onClick={logout}
          data-testid="logout-btn"
          className="w-full mt-4 py-3 rounded-full border border-red-200 text-red-600 font-semibold flex items-center justify-center gap-2 hover:bg-red-50 transition"
        >
          <LogOut size={16} /> Cerrar sesión
        </button>
      </main>
    </>
  );
}
